import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { promiseWithCatch } from '../../app.helpers';
import ProjectLinks from '../../project.links';
import { GuardShared } from '../auth/guard.shared.abstract';
import {
  allowedForAdminsOnlyPages,
  exceptionPages,
  forbiddenSocialSubpages,
  notAllowedForAdminsPages,
  onlyAllowedForAdminsPages,
} from './admin-session.auth.guard.constants';

@Injectable()
export class AdminSessionAuthLevelGuard extends GuardShared {
  protected checkGuard(previousPage?: string, route?: ActivatedRouteSnapshot): boolean {
    const isLoggedIn: boolean = this.isSessionActive();

    if (isLoggedIn && route) {
      if (this.checkAdminSessionGuard(previousPage)) {
        return true;
      } else {
        promiseWithCatch(this.router.navigateByUrl(ProjectLinks.LINK_DEFAULT));
        return false;
      }
    } else {
      if (!this.isExceptionPage(previousPage)) {
        promiseWithCatch(
          this.router.navigateByUrl(ProjectLinks.LINK_LOGIN, {
            skipLocationChange: true,
            state: { previousPage },
          }),
        );
        return false;
      }
      return true;
    }
  }

  private checkAdminSessionGuard(previousPage?: string): boolean {
    if (this.isExceptionPage(previousPage)) {
      return true;
    }

    if (!this.sessionService.isAdminSession() && this.isForAdminsOnlyPage(previousPage)) {
      promiseWithCatch(this.router.navigateByUrl(ProjectLinks.LINK_DEFAULT));
      return false;
    }

    if (this.sessionService.isAdminSession()) {
      return !this.isNotAllowedForAdminsPage(previousPage);
    } else {
      return !this.isAllowedOnlyForAdminsPage(previousPage);
    }
  }

  private isNotAllowedForAdminsPage(currentPath: string): boolean {
    return notAllowedForAdminsPages.some((page: string) => currentPath.includes(page));
  }

  private isAllowedOnlyForAdminsPage(currentPath: string): boolean {
    return allowedForAdminsOnlyPages.some((page: string) => currentPath.includes(page));
  }

  private isForAdminsOnlyPage(currentPath: string): boolean {
    return onlyAllowedForAdminsPages.some((page: string) => currentPath.includes(page));
  }

  /**
   * @description breaks target url into appropriate format and determines
   * whether it is among allowed exceptions
   * @param { string } targetPath
   * @returns { boolean }
   */
  private isExceptionPage(targetPath: string): boolean {
    const splitPath: string[] = targetPath.split('/').filter(Boolean);
    const [routePath, childPath, wildcard] = splitPath;

    if (!childPath || !wildcard || !routePath) {
      return false;
    }

    if (routePath === 'community' || routePath === 'projects') {
      return this.checkForbiddenSocialSubpages(childPath, wildcard);
    } else {
      const formattedPath = `${routePath}/${childPath}/:id`;
      return exceptionPages.some((page: string) => page === formattedPath);
    }
  }

  private checkForbiddenSocialSubpages(childPath: string, wildcard: string): boolean {
    if (forbiddenSocialSubpages.some((page: string) => page === childPath)) {
      return false;
    } else {
      return Boolean(childPath) && Boolean(wildcard);
    }
  }
}
